/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button, Popover, Form, Input, Space } from 'antd'
import classnames from 'classnames'
import { fetchVerifyCodeV3, fetchVerifyCodeShuMei } from '../../actions/main'
import * as Sentry from '@sentry/react'
import * as SentryUtils from '../../utils/sentryUtils'
import CONFIG from '../../constants/config'
import ContactUS from '../components/ContactUS'
import findPhoneNumImg from '../../images/find_phone_num.png'
import sensors from '../../utils/sensors'
import { loadSMCaptcha, loadTCaptch } from './captcha'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import { LockOutlined, MobileOutlined } from '@ant-design/icons'

// 验证码实例类型（来自第三方）
interface CaptchaInstance {
  show?: () => void
  destroy?: () => void
  verify?: () => void
  onSuccess?: (
    callback: (result: { pass: boolean; rid: string }) => void
  ) => void
  onError?: (callback: (errType: string, errMsg: string) => void) => void
  reset?: () => void
}

interface BindMobileProps {
  onSubmit: (mobile: string, verifyCode: string) => void
  onBack: () => void
}

const FindPhoneNumTip = () => {
  return (
    <div
      style={{
        width: 288,
        height: 420
      }}
    >
      <img
        src={findPhoneNumImg}
        style={{
          width: '100%'
        }}
      />
    </div>
  )
}

function BindMobile({ onSubmit, onBack }: BindMobileProps) {
  const dispatch = useAppDispatch()
  const isDesktop = useAppSelector((state) => state.main.isDesktop)

  // 本地状态
  type ValidateStatus = '' | 'success' | 'warning' | 'error' | 'validating'
  const [mobile, setMobile] = useState('')
  const [mobileHelp, setMobileHelp] = useState('')
  const [validateStatus, setValidateStatus] = useState<ValidateStatus>('')
  const [sendVerifyCodeBtnDisabled, setSendVerifyCodeBtnDisabled] =
    useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifyCodeValidateStatus, setVerifyCodeValidateStatus] =
    useState<ValidateStatus>('')
  const [verifyCodeHelp, setVerifyCodeHelp] = useState('')
  const [counting, setCounting] = useState(false)
  const [count, setCount] = useState(59)
  const [showTelHelp, setShowTelHelp] = useState(false)

  // refs
  const tencentCaptchaInstanceRef = useRef<CaptchaInstance | null>(null)
  const SMCaptchaInstanceRef = useRef<CaptchaInstance | null>(null)
  const SMCaptchaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 处理腾讯验证码回调
  const handleTCaptchaCallback = useCallback(
    (res: {
      ret: number
      ticket?: string
      randstr?: string
      errorCode?: string
      errorMessage?: string
    }) => {
      // 腾讯云验证码结果
      console.log('Bind mobile tCaptcha result', res)
      if (res.ret === 0) {
        handleFetchVerifyCode({ ticket: res.ticket, randStr: res.randstr })
        return
      }
      if (res.errorCode) {
        // 腾讯云验证码出错
        Sentry.captureMessage('Bind mobile tCaptcha error', {
          extra: {
            errorCode: res.errorCode,
            errorMessage: res.errorMessage
          }
        })
      }
    },
    [mobile]
  )

  // 路由验证码请求
  const handleRouteVerifyCodeAction = useCallback(
    (validInfo: { rid?: string; ticket?: string; randStr?: string }) => {
      // 判定是数美验证码还是腾讯云验证码，调用不同的接口
      if (validInfo.rid) {
        return dispatch(
          fetchVerifyCodeShuMei({
            mobile: mobile.trim(),
            ...validInfo
          })
        )
      } else if (validInfo.ticket) {
        return dispatch(
          fetchVerifyCodeV3({
            mobile: mobile.trim(),
            ...validInfo
          })
        )
      }
    },
    [dispatch, mobile]
  )

  // 处理倒计时清除
  const handleClearCounting = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setCounting(false)
    setCount(59)
  }, [])

  // 处理倒计时
  const handleCounting = useCallback(() => {
    setCounting(true)
    intervalRef.current = setInterval(() => {
      setCount((prevCount) => {
        if (prevCount > 0) {
          return prevCount - 1
        } else {
          handleClearCounting()
          return 59
        }
      })
    }, 1000)
  }, [handleClearCounting])

  // 获取验证码
  const handleFetchVerifyCode = useCallback(
    (validInfo: { rid?: string; ticket?: string; randStr?: string }) => {
      setVerifyCodeValidateStatus('')
      setMobileHelp('')
      setSendVerifyCodeBtnDisabled(true)
      handleRouteVerifyCodeAction(validInfo)
        .then((data) => {
          switch (data.status) {
            case 0:
              // 0 发送验证码成功
              handleCounting()
              break
            case -19:
              // -19 发送验证码失败
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送失败，请重试')
              break
            case -62:
              // 被三方验证码风控拦截
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送失败，请重试')
              break
            case -27:
              // -27 验证码发送次数已超出今日限制，请明日重试
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送次数已超出今日限制，请明日重试')
              break
            default:
              break
          }
        })
        .then(() => {
          setSendVerifyCodeBtnDisabled(false)
        })
        .catch(SentryUtils.capturePromiseException)
    },
    [handleRouteVerifyCodeAction, handleCounting]
  )

  // 初始化验证码
  useEffect(() => {
    const tCaptchaOptions = {
      loading: true,
      ready: (sdkView: unknown) => {
        // 腾讯云验证码加载成功，取消调用数美验证码的计时器
        if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
        console.log('Bind mobile tCaptcha ready', sdkView)
      }
    }

    // 加载并初始化腾讯验证码
    loadTCaptch({
      options: tCaptchaOptions,
      callback: handleTCaptchaCallback
    }).then((tCaptcha) => {
      tencentCaptchaInstanceRef.current = tCaptcha as CaptchaInstance
    })

    // 加载并初始化数美验证码
    loadSMCaptcha().then((SMCaptcha) => {
      SMCaptchaInstanceRef.current = SMCaptcha as CaptchaInstance
      SMCaptchaInstanceRef.current.onSuccess?.((result) => {
        console.log('Bind mobile SMCaptcha result', result)
        SMCaptchaInstanceRef.current?.reset?.()
        //{rid: '202308292057369e13a0876b880f8eab', pass: true}
        if (result.pass) {
          handleFetchVerifyCode({
            rid: result.rid
          })
        }
      })
      SMCaptchaInstanceRef.current.onError?.((errType, errMsg) => {
        SMCaptchaInstanceRef.current?.reset?.()
        Sentry.captureMessage('Bind mobile SMCaptcha error', {
          extra: {
            errType,
            errMsg
          }
        })
      })
    })

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
    }
  }, [handleTCaptchaCallback, handleFetchVerifyCode])

  // 验证码校验
  const handleCaptchaValidate = useCallback(() => {
    // 先调用腾讯验证码，启动计时器，5秒后销毁腾讯云验证码，调用数美验证码
    tencentCaptchaInstanceRef.current?.show?.()

    if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
    SMCaptchaTimerRef.current = setTimeout(() => {
      tencentCaptchaInstanceRef.current?.destroy?.()
      SMCaptchaInstanceRef.current?.verify?.()
    }, 5000)
  }, [])

  // 检查手机号
  const handleCheckMobile = useCallback(() => {
    sensors.track('web_click_page_workgroup_login_bind_phone', {
      clickItem: 'getCode'
    })

    if (CONFIG.mobileRegex.test(mobile.trim())) {
      setValidateStatus('success')
      setMobileHelp('')
      handleCaptchaValidate()
    } else {
      setValidateStatus('error')
      setMobileHelp('请输入正确的手机号')
      setSendVerifyCodeBtnDisabled(false)
    }
  }, [mobile, handleCaptchaValidate])

  // 提交
  const handleSubmit = useCallback(() => {
    sensors.track('web_click_page_workgroup_login_bind_phone', {
      clickItem: 'confirm',
      phoneNum: mobile
    })

    onSubmit(mobile, verifyCode)
  }, [mobile, verifyCode, onSubmit])

  // 渲染帮助
  const renderHelp = () => {
    return (
      <>
        <div
          style={{
            alignSelf: 'start',
            paddingTop: 16,
            paddingLeft: 20
          }}
        >
          <a
            onClick={() => {
              setShowTelHelp(false)
            }}
          >
            返回
          </a>
        </div>

        <img
          src={findPhoneNumImg}
          style={{
            marginTop: 16,
            width: 248,
            height: 357,
            display: 'block'
          }}
        />

        <div
          style={{
            background: '#0093FF',
            borderRadius: 4,
            color: '#fff',
            fontSize: 17,
            fontWeight: 'bold',
            padding: '12px 0',
            width: 274,
            textAlign: 'center',
            marginTop: 20,
            cursor: 'pointer'
          }}
          onClick={() => {
            setShowTelHelp(false)
          }}
        >
          去输入手机号
        </div>
      </>
    )
  }

  // 渲染绑定表单
  const renderBindingForm = () => {
    return (
      <>
        <div
          style={{
            alignSelf: 'start',
            paddingTop: 16,
            paddingLeft: 20
          }}
        >
          <a
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: '#0070F4'
            }}
          >
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.76777 0.883883L1.76777 5.88388L6.76777 10.8839L5.88388 11.7678L0 5.88388L5.88388 0L6.76777 0.883883Z"
                fill="#0070F4"
              />
            </svg>
            <span style={{ marginLeft: 8 }}>重新扫码</span>
          </a>
        </div>

        <div className="binding">
          <div className="title">绑定手机号，下次扫码直接登录</div>

          <Form className="login-form">
            <Form.Item validateStatus={validateStatus} help={mobileHelp}>
              <Space.Compact block>
                <Input
                  size="large"
                  readOnly
                  tabIndex={-1}
                  value="+86"
                  style={{
                    width: 53,
                    flex: 'none',
                    textAlign: 'center',
                    background: 'rgba(0,0,0,0.02)',
                    color: 'rgba(0,0,0,0.88)',
                    cursor: 'default',
                    pointerEvents: 'none'
                  }}
                />
                <Input
                  size="large"
                  prefix={
                    <MobileOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  height={36}
                  placeholder="手机号"
                  value={mobile}
                  onFocus={() => {
                    sensors.track('web_click_page_workgroup_login_bind_phone', {
                      clickItem: 'phoneNum'
                    })
                  }}
                  onChange={(e) => {
                    setMobile(e.target.value.trim())
                    setValidateStatus('')
                    setMobileHelp('')
                  }}
                />
              </Space.Compact>
            </Form.Item>
            <Form.Item
              validateStatus={verifyCodeValidateStatus}
              help={verifyCodeHelp}
            >
              <Input
                size="large"
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="验证码"
                pattern="[0-9]*"
                maxLength={4}
                suffix={
                  counting ? (
                    <span className="count">重新获取（{count}秒）</span>
                  ) : (
                    <a
                      onClick={
                        sendVerifyCodeBtnDisabled
                          ? undefined
                          : handleCheckMobile
                      }
                      style={{
                        cursor: sendVerifyCodeBtnDisabled
                          ? 'not-allowed'
                          : 'pointer',
                        opacity: sendVerifyCodeBtnDisabled ? 0.5 : 1
                      }}
                    >
                      获取验证码
                    </a>
                  )
                }
                value={verifyCode}
                onChange={(e) => {
                  setVerifyCode(e.target.value.trim())
                  setVerifyCodeValidateStatus('')
                  setVerifyCodeHelp('')
                }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                className="login-button"
                type="primary"
                disabled={!mobile.trim() || verifyCode.length !== 4}
                onClick={handleSubmit}
              >
                完成
              </Button>
            </Form.Item>
          </Form>

          <div className="help">
            {isDesktop ? (
              <a
                onClick={() => {
                  sensors.track('web_click_page_workgroup_login_bind_phone', {
                    clickItem: 'noPhoneNum'
                  })

                  setShowTelHelp(true)
                }}
              >
                找不到我的手机号？
              </a>
            ) : (
              <Popover placement="right" content={<FindPhoneNumTip />}>
                <a
                  onMouseOver={() => {
                    sensors.track('web_click_page_workgroup_login_bind_phone', {
                      clickItem: 'noPhoneNum'
                    })
                  }}
                >
                  找不到我的手机号？
                </a>
              </Popover>
            )}
          </div>

          <div className="contact-us">
            <ContactUS
              fromPage="绑定手机号"
              track={() => {
                sensors.track('web_click_page_workgroup_login_bind_phone', {
                  clickItem: 'contactUs'
                })
              }}
            >
              <a>联系客服</a>
            </ContactUS>
          </div>
        </div>
      </>
    )
  }

  return (
    <div
      className={classnames('panel', {
        animated: !isDesktop,
        zoomIn: !isDesktop
      })}
      key="login-panel"
    >
      {showTelHelp ? renderHelp() : renderBindingForm()}
    </div>
  )
}

export default BindMobile
